import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DatePicker from 'react-datepicker';
import Select from 'react-select';
import 'react-datepicker/dist/react-datepicker.css';
import Swal from 'sweetalert2';
import axiosInstance from '../utils/axiosInstance';
import TvecDocuments from './TvecDocuments';
import { Field, Modal, inputClass } from './TvecForm';
import { downloadCleaningChartPdf } from '../utils/tvecCleaningPdf';

const API = '/admin/tvec-cleaning';
const SAVED_WASHROOM_KEY = 'tvecCleaningWashroom';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

// The sheet exactly as supplied: Morning / Mid Day / Evening, four ticks each.
const SESSIONS = [
  { key: 'm', label: 'Morning' },
  { key: 'd', label: 'Mid Day' },
  { key: 'e', label: 'Evening' },
];
const ITEMS = [
  { key: 'handwash', label: 'Handwash' },
  { key: 'hygiene', label: 'Hygiene' },
  { key: 'basin', label: 'Wash basin cleaned' },
  { key: 'damage', label: 'Damage / Leakage' },
];
const CLEANING_KEYS = SESSIONS.flatMap((s) => ITEMS.filter((i) => i.key !== 'damage').map((i) => `${s.key}_${i.key}`));
const isComplete = (day) => CLEANING_KEYS.every((k) => day.checks[k]);

const pad = (n) => String(n).padStart(2, '0');
const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
// The month is finished once the day after its last day has come.
const monthIsOver = (year, month) => todayIso() > `${year}-${pad(month)}-${pad(new Date(year, month, 0).getDate())}`;

const formatDay = (iso) => {
  const d = new Date(`${iso}T00:00:00`);
  return {
    date: d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }),
    weekday: d.toLocaleDateString('en-GB', { weekday: 'short' }),
    sunday: d.getDay() === 0,
  };
};

const errorText = (err, fallback) => err?.response?.data?.message || fallback;
const readSavedWashroom = () => {
  try { return Number(window.localStorage.getItem(SAVED_WASHROOM_KEY)) || null; } catch { return null; }
};
const writeSavedWashroom = (id) => {
  try { window.localStorage.setItem(SAVED_WASHROOM_KEY, String(id)); } catch { /* storage can be blocked */ }
};
const toast = (icon, title) => Swal.fire({ toast: true, position: 'top-end', icon, title, showConfirmButton: false, timer: 2200 });

const btn = 'rounded-lg border px-3 py-1.5 text-xs font-semibold transition disabled:cursor-not-allowed disabled:opacity-50';
const btnNeutral = `${btn} border-slate-200 text-slate-600 hover:bg-slate-100`;
const btnPrimary = `${btn} border-sky-600 bg-sky-600 text-white hover:bg-sky-700`;
const btnDanger = `${btn} border-rose-200 text-rose-600 hover:bg-rose-50`;

// ---- Cleaning staff (names only) -------------------------------------------

function CleanerManager({ cleaners, setCleaners }) {
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const add = async (e) => {
    e.preventDefault();
    if (saving) return;
    if (!name.trim()) { setError('Enter a name.'); return; }
    setSaving(true);
    try {
      const res = await axiosInstance.post(`${API}/cleaners`, { name });
      setCleaners((prev) => [...prev, res.data.data].sort((a, b) => Number(b.isActive) - Number(a.isActive) || a.name.localeCompare(b.name)));
      setName('');
      setError('');
    } catch (err) {
      setError(err?.response?.data?.errors?.name || errorText(err, 'Could not add the name.'));
    }
    setSaving(false);
  };

  const toggle = async (c) => {
    try {
      const res = await axiosInstance.put(`${API}/cleaners/${c.id}`, { name: c.name, isActive: !c.isActive });
      setCleaners((prev) => prev.map((x) => (x.id === c.id ? res.data.data : x)));
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Could not save', text: errorText(err, 'Please try again.') });
    }
  };

  const remove = async (c) => {
    const result = await Swal.fire({ icon: 'warning', title: 'Delete this name?', text: c.name, showCancelButton: true, confirmButtonText: 'Delete', confirmButtonColor: '#dc2626' });
    if (!result.isConfirmed) return;
    try {
      await axiosInstance.delete(`${API}/cleaners/${c.id}`);
      setCleaners((prev) => prev.filter((x) => x.id !== c.id));
    } catch (err) {
      Swal.fire({ icon: 'info', title: 'Cannot delete', text: errorText(err, 'Please try again.') });
    }
  };

  return (
    <details className="group rounded-2xl border border-slate-200 bg-white shadow-sm">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-5 py-4">
        <span>
          <span className="block text-base font-semibold text-slate-900">Cleaning staff ({cleaners.filter((c) => c.isActive).length})</span>
          <span className="block text-sm text-slate-500">The names offered in the “Checked By” dropdown.</span>
        </span>
        <span aria-hidden="true" className="text-slate-400 transition group-open:rotate-180">⌄</span>
      </summary>
      <div className="space-y-4 border-t border-slate-100 p-5">
        <form onSubmit={add} noValidate className="flex flex-wrap items-start gap-2">
          <div className="min-w-[14rem] flex-1">
            <input type="text" value={name} maxLength={150} placeholder="Add a cleaning staff name" aria-label="Cleaning staff name" onChange={(e) => { setName(e.target.value); setError(''); }} className={inputClass(error)} />
            {error ? <p className="mt-1 text-xs text-rose-600">{error}</p> : null}
          </div>
          <button type="submit" disabled={saving} className={`${btnPrimary} py-2.5`}>{saving ? 'Adding…' : 'Add name'}</button>
        </form>
        {cleaners.length === 0 ? (
          <p className="text-sm text-slate-500">No names yet. Add the cleaners’ names above.</p>
        ) : (
          <ul className="divide-y divide-slate-100 rounded-xl border border-slate-200">
            {cleaners.map((c) => (
              <li key={c.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5">
                <span className={`text-sm font-medium ${c.isActive ? 'text-slate-800' : 'text-slate-400'}`}>
                  {c.name}{!c.isActive ? <span className="ml-2 rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-slate-600">Inactive</span> : null}
                </span>
                <span className="flex gap-1.5">
                  <button type="button" onClick={() => toggle(c)} className={btnNeutral}>{c.isActive ? 'Mark inactive' : 'Mark active'}</button>
                  <button type="button" onClick={() => remove(c)} className={btnDanger}>Delete</button>
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </details>
  );
}

// ---- Quick add washroom ----------------------------------------------------

function AddWashroomModal({ onClose, onAdded }) {
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (saving) return;
    if (!name.trim()) { setError('Enter a washroom name.'); return; }
    setSaving(true);
    try {
      const res = await axiosInstance.post(`${API}/washrooms`, { name });
      onAdded(res.data.data, res.data.existed);
    } catch (err) {
      setError(err?.response?.data?.errors?.name || errorText(err, 'Could not add the washroom.'));
      setSaving(false);
    }
  };

  return (
    <Modal title="Add washroom" subtitle="For example: Ground floor – Gents." onClose={onClose} onSubmit={submit} saving={saving} submitLabel="Add washroom">
      <Field label="Washroom name" required error={error}>
        <input autoFocus type="text" value={name} maxLength={150} placeholder="e.g. Ground floor – Gents" onChange={(e) => { setName(e.target.value); setError(''); }} className={inputClass(error)} />
      </Field>
    </Modal>
  );
}

// ---- Quick add cleaning staff ----------------------------------------------

function AddCleanerModal({ onClose, onAdded }) {
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (saving) return;
    if (!name.trim()) { setError('Enter the cleaner’s name.'); return; }
    setSaving(true);
    try {
      const res = await axiosInstance.post(`${API}/cleaners`, { name });
      onAdded(res.data.data);
    } catch (err) {
      setError(err?.response?.data?.errors?.name || errorText(err, 'Could not add the name.'));
      setSaving(false);
    }
  };

  return (
    <Modal title="Add cleaning staff" subtitle="The name will be available in every “Checked By” dropdown." onClose={onClose} onSubmit={submit} saving={saving} submitLabel="Add">
      <Field label="Name" required error={error}>
        <input autoFocus type="text" value={name} maxLength={150} placeholder="Cleaner’s name" onChange={(e) => { setName(e.target.value); setError(''); }} className={inputClass(error)} />
      </Field>
    </Modal>
  );
}

// ---- The monthly sheet -----------------------------------------------------

const ADD_NEW = '__add_new__';

const personStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 30,
    fontSize: 12,
    borderRadius: 6,
    borderColor: state.isFocused ? '#0284c7' : '#cbd5e1',
    boxShadow: state.isFocused ? '0 0 0 2px #e0f2fe' : 'none',
    '&:hover': { borderColor: state.isFocused ? '#0284c7' : '#94a3b8' },
  }),
  valueContainer: (base) => ({ ...base, padding: '0 6px' }),
  dropdownIndicator: (base) => ({ ...base, padding: 4 }),
  clearIndicator: (base) => ({ ...base, padding: 4 }),
  indicatorSeparator: () => ({ display: 'none' }),
  placeholder: (base) => ({ ...base, color: '#94a3b8' }),
  option: (base) => ({ ...base, fontSize: 12, padding: '6px 10px' }),
  menuPortal: (base) => ({ ...base, zIndex: 200 }),
};

// Searchable dropdown (react-select). onAddNew (optional) adds a last option that opens the
// quick-add form instead of choosing a person. valueLabel names the current person when
// they are no longer in the options (e.g. a cleaner marked inactive).
function PersonSelect({ value, options, onChange, label, onAddNew, valueLabel }) {
  const all = onAddNew ? [...options, { value: ADD_NEW, label: '＋ Add new cleaner…', isAdd: true }] : options;
  const selected = options.find((o) => o.value === value) || (value ? { value, label: valueLabel || `#${value}` } : null);
  return (
    <Select
      value={selected}
      options={all}
      onChange={(o) => {
        if (!o) onChange(null);
        else if (o.value === ADD_NEW) onAddNew();
        else onChange(o.value);
      }}
      isClearable
      placeholder="Select…"
      aria-label={label}
      noOptionsMessage={() => 'No match'}
      menuPortalTarget={document.body}
      menuPosition="fixed"
      closeMenuOnScroll={(e) => !(e.target instanceof Element && e.target.closest('[class*="-menu"]'))}
      formatOptionLabel={(o) => (o.isAdd ? <span className="font-semibold text-sky-700">{o.label}</span> : o.label)}
      styles={personStyles}
    />
  );
}

// One "Checked By" / "Supervised By" cell: the dropdown plus a button that copies the
// person to every later day of the month.
function PersonCell({ day, field, nameField, options, label, onChange, onAddNew, onApplyRest, isLast }) {
  const person = day[field];
  return (
    <div className="flex items-center gap-1">
      <div className="min-w-0 flex-1 text-left">
        <PersonSelect value={person} valueLabel={day[nameField]} options={options} label={label} onChange={onChange} onAddNew={onAddNew} />
      </div>
      <button
        type="button"
        disabled={!person || isLast}
        onClick={() => onApplyRest(day, field)}
        title={isLast ? 'This is the last day of the month' : person ? 'Apply this person to the rest of the month' : 'Select a person first'}
        aria-label={`Apply to the rest of the month, ${label}`}
        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-slate-200 text-sm font-bold text-sky-700 transition hover:bg-sky-50 disabled:cursor-not-allowed disabled:text-slate-300 disabled:hover:bg-transparent"
      >
        ↓
      </button>
    </div>
  );
}

const HEAD_ROW_HEIGHT = 36;

function ChartSheet({ chart, cleaners, staff, signedCount, onPatchDay, onFill, onFillAfter, onDelete, onCleanerAdded }) {
  const scrollRef = useRef(null);
  const [bulk, setBulk] = useState({ checkedById: null, supervisedById: null });
  const [filling, setFilling] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [addingFor, setAddingFor] = useState(null); // null | { day? } — day is absent for the bulk selector
  const today = todayIso();
  const hasToday = chart.days.some((d) => d.date === today);
  const lastDate = chart.days[chart.days.length - 1]?.date;

  const cleanerOptions = (day) => cleaners.filter((c) => c.isActive || c.id === day?.checkedById).map((c) => ({ value: c.id, label: c.name }));
  const staffOptions = staff.map((s) => ({ value: s.staffId, label: s.name }));

  const jumpToToday = useCallback(() => {
    const row = scrollRef.current?.querySelector(`[data-date="${today}"]`);
    if (row && scrollRef.current) {
      scrollRef.current.scrollTo({ top: Math.max(0, row.offsetTop - HEAD_ROW_HEIGHT * 3), behavior: 'smooth' });
    }
  }, [today]);

  // Opening the current month lands on today's row.
  useEffect(() => {
    if (hasToday) {
      const id = setTimeout(jumpToToday, 150);
      return () => clearTimeout(id);
    }
    return undefined;
  }, [chart.id, hasToday, jumpToToday]);

  const fill = async (scope) => {
    if (!bulk.checkedById && !bulk.supervisedById) {
      Swal.fire({ icon: 'info', title: 'Select a person', text: 'Choose who checked and/or supervised, then apply.' });
      return;
    }
    if (scope === 'all') {
      const result = await Swal.fire({ icon: 'question', title: 'Apply to every day?', text: 'This replaces the names already set on this chart.', showCancelButton: true, confirmButtonText: 'Apply to all' });
      if (!result.isConfirmed) return;
    }
    setFilling(true);
    await onFill({ ...bulk, scope });
    setFilling(false);
  };

  // Copies one day's person to every later day. Days that already hold someone else are
  // only replaced after the user agrees.
  const applyRest = async (day, field) => {
    const later = chart.days.filter((d) => d.date > day.date);
    if (!later.length) return;
    const conflicts = later.filter((d) => d[field] && d[field] !== day[field]).length;
    let overwrite = false;
    if (conflicts) {
      const result = await Swal.fire({
        icon: 'question',
        title: 'Replace the other names?',
        text: `${conflicts} later ${conflicts === 1 ? 'day has' : 'days have'} a different person. Replace ${conflicts === 1 ? 'it' : 'them'} too, or only fill the empty days?`,
        showCancelButton: true,
        showDenyButton: true,
        confirmButtonText: 'Replace all',
        denyButtonText: 'Only empty days',
      });
      if (result.isDismissed) return;
      overwrite = result.isConfirmed;
    }
    await onFillAfter({ dayId: day.id, field, overwrite });
  };

  // Header button: takes the person of the latest day that has one and fills the days after it.
  const fillRest = (field) => {
    const last = [...chart.days].reverse().find((d) => d[field]);
    if (!last) { toast('info', 'Select a person on a day first'); return; }
    if (last.date === chart.days[chart.days.length - 1].date) { toast('info', 'Nothing left to fill'); return; }
    onFillAfter({ dayId: last.id, field, overwrite: false });
  };

  const downloadPdf = async () => {
    if (pdfBusy) return;
    setPdfBusy(true);
    try {
      await downloadCleaningChartPdf(chart);
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Could not create the PDF', text: 'Please try again.' });
    }
    setPdfBusy(false);
  };

  const percent = chart.totalDays ? Math.round((chart.doneDays / chart.totalDays) * 100) : 0;
  const over = monthIsOver(chart.year, chart.month);
  const head = 'border border-slate-400 bg-slate-100 px-2 text-center text-xs font-bold text-slate-900';
  const cell = 'border border-slate-300 px-1.5 py-1 text-center';

  return (
    <section className="space-y-4">
      {/* Summary */}
      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="text-lg font-bold text-slate-900">{chart.washroomName}</h3>
            <p className="text-sm text-slate-500">{MONTHS[chart.month - 1]} {chart.year}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {signedCount > 0 ? (
              <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-200">✓ Signed copy uploaded</span>
            ) : over ? (
              <span className="rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700 ring-1 ring-inset ring-amber-200">Signed copy pending</span>
            ) : (
              <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-500">Signed copy after month end</span>
            )}
            {hasToday ? <button type="button" onClick={jumpToToday} className={btnNeutral}>Jump to today</button> : null}
            <button type="button" onClick={downloadPdf} disabled={pdfBusy} title="Download the sheet as a PDF for printing: A4, landscape" className={`${btn} border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100`}>
              {pdfBusy ? 'Preparing PDF…' : '⬇ Download PDF (A4)'}
            </button>
          </div>
        </div>
        <div className="mt-4">
          <div className="flex items-center justify-between text-xs text-slate-600">
            <span>{chart.doneDays} of {chart.totalDays} days complete</span>
            <span className="font-semibold">{percent}%</span>
          </div>
          <div className="mt-1 h-2 overflow-hidden rounded-full bg-slate-100" role="progressbar" aria-valuenow={percent} aria-valuemin={0} aria-valuemax={100}>
            <div className={`h-full transition-all ${percent === 100 ? 'bg-emerald-500' : 'bg-sky-500'}`} style={{ width: `${percent}%` }} />
          </div>
        </div>
        <div className="mt-4 flex flex-wrap items-end gap-3 border-t border-slate-100 pt-4">
          <div className="block min-w-[12rem] flex-1 text-xs font-medium text-slate-700">
            <span className="flex items-center justify-between">
              <span>Checked By (all days)</span>
              <button type="button" onClick={() => setAddingFor({})} className="font-semibold text-sky-700 hover:underline">+ Add cleaner</button>
            </span>
            <div className="mt-1"><PersonSelect value={bulk.checkedById} options={cleanerOptions()} label="Checked by for all days" onChange={(v) => setBulk((p) => ({ ...p, checkedById: v }))} onAddNew={() => setAddingFor({})} /></div>
          </div>
          <div className="block min-w-[12rem] flex-1 text-xs font-medium text-slate-700">
            Supervised By (all days)
            <div className="mt-1"><PersonSelect value={bulk.supervisedById} options={staffOptions} label="Supervised by for all days" onChange={(v) => setBulk((p) => ({ ...p, supervisedById: v }))} /></div>
          </div>
          <div className="flex gap-1.5">
            <button type="button" disabled={filling} onClick={() => fill('empty')} className={btnPrimary}>Apply to empty days</button>
            <button type="button" disabled={filling} onClick={() => fill('all')} className={btnNeutral}>Apply to all days</button>
          </div>
        </div>
      </div>

      {/* Sheet */}
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="px-5 pt-5 text-center">
          <h4 className="text-sm font-bold uppercase text-slate-900 sm:text-base">ESOFT METRO CAMPUS – ROUTINE TOILET CLEANING &amp; INSPECTION</h4>
          <p className="mt-1 text-sm font-semibold text-slate-700">Daily Routine Monitoring Sheet</p>
        </div>
        <div ref={scrollRef} className="relative mt-4 max-h-[70vh] overflow-auto px-3 pb-4 sm:px-5">
          <table className="w-full min-w-[1150px] border-separate border-spacing-0 text-xs text-slate-900">
            <thead>
              <tr style={{ height: HEAD_ROW_HEIGHT }}>
                <th rowSpan={2} className={`${head} sticky left-0 top-0 z-30 min-w-[9.5rem]`}>Date</th>
                {SESSIONS.map((s) => <th key={s.key} colSpan={ITEMS.length} className={`${head} sticky top-0 z-20`}>{s.label}</th>)}
                {[['checkedById', 'Checked By'], ['supervisedById', 'Supervised By']].map(([field, title]) => (
                  <th key={field} rowSpan={2} className={`${head} sticky top-0 z-20 min-w-[14rem]`}>
                    <div>{title}</div>
                    <button
                      type="button"
                      onClick={() => fillRest(field)}
                      title="Apply the last selected person to every day after it"
                      className="mt-1 rounded-md border border-sky-200 bg-white px-2 py-0.5 text-[10px] font-semibold text-sky-700 hover:bg-sky-50"
                    >
                      ↓ Fill rest of month
                    </button>
                  </th>
                ))}
              </tr>
              <tr>
                {SESSIONS.flatMap((s) => ITEMS.map((i) => (
                  <th key={`${s.key}${i.key}`} className={`${head} sticky z-20 py-1 font-semibold`} style={{ top: HEAD_ROW_HEIGHT }}>{i.label}</th>
                )))}
              </tr>
            </thead>
            <tbody>
              {chart.days.map((day) => {
                const f = formatDay(day.date);
                const complete = isComplete(day);
                const isToday = day.date === today;
                const missed = !complete && day.date < today;
                const rowBg = isToday ? 'bg-sky-50' : f.sunday ? 'bg-slate-50' : 'bg-white';
                return (
                  <tr key={day.id} data-date={day.date} className={rowBg}>
                    <td className={`${cell} sticky left-0 z-10 whitespace-nowrap text-left ${rowBg} ${isToday ? 'border-l-4 border-l-sky-500' : ''}`}>
                      <span className="flex items-center justify-between gap-2">
                        <span>
                          <span className="font-medium">{f.date}</span> <span className="text-[10px] text-slate-400">{f.weekday}</span>
                          {isToday ? <span className="ml-1 rounded bg-sky-600 px-1 py-0.5 text-[9px] font-bold uppercase text-white">Today</span> : null}
                        </span>
                        <button
                          type="button"
                          onClick={() => onPatchDay(day, { checks: Object.fromEntries(CLEANING_KEYS.map((k) => [k, !complete])) })}
                          title={complete ? 'Clear the cleaning ticks for this day' : 'Tick Handwash, Hygiene and Wash basin cleaned for the whole day'}
                          aria-label={`${complete ? 'Clear' : 'Tick all'} cleaning checks, ${f.date}`}
                          className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[11px] font-bold transition ${complete ? 'bg-emerald-500 text-white hover:bg-emerald-600' : missed ? 'bg-amber-100 text-amber-700 hover:bg-amber-200' : 'bg-slate-100 text-slate-400 hover:bg-sky-100 hover:text-sky-700'}`}
                        >
                          ✓
                        </button>
                      </span>
                    </td>
                    {SESSIONS.flatMap((s) => ITEMS.map((i) => {
                      const key = `${s.key}_${i.key}`;
                      return (
                        <td key={key} className={cell}>
                          <input
                            type="checkbox"
                            checked={Boolean(day.checks[key])}
                            aria-label={`${f.date} ${s.label} ${i.label}`}
                            onChange={(e) => onPatchDay(day, { checks: { [key]: e.target.checked } })}
                            className={`h-4 w-4 cursor-pointer ${i.key === 'damage' ? 'accent-rose-600' : 'accent-sky-600'}`}
                          />
                        </td>
                      );
                    }))}
                    <td className={cell}>
                      <PersonCell
                        day={day}
                        field="checkedById"
                        nameField="checkedByName"
                        options={cleanerOptions(day)}
                        label={`Checked by, ${f.date}`}
                        onChange={(v) => onPatchDay(day, { checkedById: v })}
                        onAddNew={() => setAddingFor({ day })}
                        onApplyRest={applyRest}
                        isLast={day.date === lastDate}
                      />
                    </td>
                    <td className={cell}>
                      <PersonCell
                        day={day}
                        field="supervisedById"
                        nameField="supervisedByName"
                        options={staffOptions}
                        label={`Supervised by, ${f.date}`}
                        onChange={(v) => onPatchDay(day, { supervisedById: v })}
                        onApplyRest={applyRest}
                        isLast={day.date === lastDate}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 px-5 py-3">
          <p className="text-xs text-slate-500">
            Changes save automatically. The round ✓ button ticks Handwash, Hygiene and Wash basin cleaned for the whole day; a day is complete when all nine are ticked.
          </p>
          <button type="button" onClick={onDelete} className={btnDanger}>Delete this chart</button>
        </div>
      </div>

      {addingFor ? (
        <AddCleanerModal
          onClose={() => setAddingFor(null)}
          onAdded={(cleaner) => {
            onCleanerAdded(cleaner);
            // The new person is picked straight away where the user was.
            if (addingFor.day) onPatchDay(addingFor.day, { checkedById: cleaner.id });
            else setBulk((p) => ({ ...p, checkedById: cleaner.id }));
            setAddingFor(null);
            toast('success', `${cleaner.name} added`);
          }}
        />
      ) : null}
    </section>
  );
}

// ---- Page section ----------------------------------------------------------

export default function TvecCleaningCharts() {
  const now = new Date();
  const [washrooms, setWashrooms] = useState([]);
  const [washroomId, setWashroomId] = useState(null);
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [cleaners, setCleaners] = useState([]);
  const [staff, setStaff] = useState([]);
  const [summaries, setSummaries] = useState([]);
  const [loadedKey, setLoadedKey] = useState('');
  const [chart, setChart] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [addingWashroom, setAddingWashroom] = useState(false);
  const [loadError, setLoadError] = useState('');

  const washroom = useMemo(() => washrooms.find((w) => w.id === washroomId) || null, [washrooms, washroomId]);
  const key = washroomId ? `${washroomId}:${year}` : '';
  const selection = useRef({ washroomId, year, month });
  selection.current = { washroomId, year, month };

  const refreshWashrooms = useCallback(async () => {
    try {
      const res = await axiosInstance.get(`${API}/washrooms`);
      setWashrooms(res.data.data ?? []);
    } catch (err) { /* the list stays as it was */ }
  }, []);

  useEffect(() => {
    let cancelled = false;
    Promise.all([axiosInstance.get(`${API}/washrooms`), axiosInstance.get(`${API}/cleaners`), axiosInstance.get(`${API}/staff-options`)])
      .then(([w, c, s]) => {
        if (cancelled) return;
        const list = w.data.data ?? [];
        setWashrooms(list);
        setCleaners(c.data.data ?? []);
        setStaff(s.data.data ?? []);
        setWashroomId((list.find((x) => x.id === readSavedWashroom()) || list[0])?.id ?? null);
      })
      .catch(() => { if (!cancelled) setLoadError('Failed to load the cleaning chart data.'); })
      .finally(() => { if (!cancelled) setInitialLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const loadSummaries = useCallback(async (id, y) => {
    if (!id) return;
    try {
      const res = await axiosInstance.get(`${API}/charts`, { params: { year: y, washroomId: id } });
      if (selection.current.washroomId === id && selection.current.year === y) {
        setSummaries(res.data.data ?? []);
        setLoadedKey(`${id}:${y}`);
      }
    } catch (err) {
      setLoadError('Failed to load the year overview.');
    }
  }, []);

  useEffect(() => { loadSummaries(washroomId, year); }, [washroomId, year, loadSummaries]);

  const summaryByMonth = useMemo(() => new Map(summaries.map((s) => [s.month, s])), [summaries]);
  const current = key && loadedKey === key ? summaryByMonth.get(month) : undefined;
  const monthReady = Boolean(key) && loadedKey === key;

  const openChart = useCallback(async (id) => {
    try {
      const res = await axiosInstance.get(`${API}/charts/${id}`);
      const data = res.data.data;
      const sel = selection.current;
      if (data.washroomId === sel.washroomId && data.year === sel.year && data.month === sel.month) setChart(data);
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Could not open the chart', text: errorText(err, 'Please try again.') });
    }
  }, []);

  // The chart of the chosen washroom + month opens by itself when it exists.
  useEffect(() => {
    if (!key) { setChart(null); return; }
    if (loadedKey !== key) return;
    if (!current) { setChart(null); return; }
    if (chart?.id !== current.id) openChart(current.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, loadedKey, current?.id, openChart]);

  const shownChart = chart && chart.washroomId === washroomId && chart.year === year && chart.month === month ? chart : null;
  const signedCount = current?.signedCount ?? 0;

  const chooseWashroom = (id) => { setWashroomId(id); writeSavedWashroom(id); };
  const goToMonth = (y, m) => {
    // Months wrap into the neighbouring year.
    const d = new Date(y, m - 1, 1);
    setYear(d.getFullYear());
    setMonth(d.getMonth() + 1);
  };

  const generate = async () => {
    if (generating || !washroomId) return;
    setGenerating(true);
    try {
      const res = await axiosInstance.post(`${API}/charts`, { washroomId, year, month });
      setChart(res.data.data);
      await Promise.all([loadSummaries(washroomId, year), refreshWashrooms()]);
      toast('success', `${MONTHS[month - 1]} ${year} chart generated`);
    } catch (err) {
      const data = err?.response?.data;
      if (err?.response?.status === 409 && data?.id) {
        await loadSummaries(washroomId, year);
        await openChart(data.id);
      } else {
        Swal.fire({ icon: 'error', title: 'Could not generate', text: data?.message || 'Please try again.' });
      }
    }
    setGenerating(false);
  };

  // Saves straight away; the page updates first and is put back if the save fails.
  const patchDay = async (day, payload) => {
    const chartId = chart.id;
    const apply = (c, d) => ({ ...c, days: c.days.map((x) => (x.id === day.id ? d : x)) });
    const optimistic = {
      ...day,
      checks: { ...day.checks, ...(payload.checks || {}) },
      ...('checkedById' in payload ? { checkedById: payload.checkedById } : {}),
      ...('supervisedById' in payload ? { supervisedById: payload.supervisedById } : {}),
    };
    setChart((c) => (c && c.id === chartId ? apply(c, optimistic) : c));
    try {
      const res = await axiosInstance.patch(`${API}/charts/${chartId}/days/${day.id}`, payload);
      setChart((c) => (c && c.id === chartId ? { ...apply(c, res.data.data), doneDays: res.data.summary.doneDays } : c));
      setSummaries((prev) => prev.map((s) => (s.id === chartId ? { ...s, doneDays: res.data.summary.doneDays } : s)));
    } catch (err) {
      setChart((c) => (c && c.id === chartId ? apply(c, day) : c));
      toast('error', errorText(err, 'Could not save.'));
    }
  };

  const fillPeople = async (payload) => {
    try {
      const res = await axiosInstance.post(`${API}/charts/${chart.id}/fill`, payload);
      setChart(res.data.data);
      toast('success', 'Names applied');
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Could not save', text: errorText(err, 'Please try again.') });
    }
  };

  const fillAfter = async (payload) => {
    try {
      const res = await axiosInstance.post(`${API}/charts/${chart.id}/fill-after`, payload);
      setChart(res.data.data);
      toast('success', 'Applied to the rest of the month');
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Could not save', text: errorText(err, 'Please try again.') });
    }
  };

  const deleteChart = async () => {
    const result = await Swal.fire({
      icon: 'warning',
      title: 'Delete this chart?',
      text: `${chart.washroomName} · ${MONTHS[chart.month - 1]} ${chart.year}. All ticks and names on it will be lost.`,
      showCancelButton: true,
      confirmButtonText: 'Delete',
      confirmButtonColor: '#dc2626',
    });
    if (!result.isConfirmed) return;
    try {
      await axiosInstance.delete(`${API}/charts/${chart.id}`);
      setChart(null);
      await Promise.all([loadSummaries(washroomId, year), refreshWashrooms()]);
    } catch (err) {
      Swal.fire({ icon: 'info', title: 'Cannot delete', text: errorText(err, 'Please try again.') });
    }
  };

  if (initialLoading) {
    return <div className="space-y-4">{[0, 1].map((i) => <div key={i} className="h-28 animate-pulse rounded-2xl border border-slate-200 bg-white" />)}</div>;
  }

  return (
    <div className="space-y-5">
      {loadError ? <p className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">{loadError}</p> : null}

      {/* 1 · Washroom tabs + month picker */}
      <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Washroom</h3>
        <div role="tablist" aria-label="Washrooms" className="mt-2 flex flex-wrap gap-2">
          {washrooms.map((w) => {
            const active = w.id === washroomId;
            return (
              <button
                key={w.id}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => chooseWashroom(w.id)}
                className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition ${active ? 'border-sky-600 bg-sky-600 text-white shadow-sm' : 'border-slate-200 bg-white text-slate-700 hover:border-sky-300 hover:bg-sky-50'}`}
              >
                {w.name}
                <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${active ? 'bg-white/25 text-white' : 'bg-slate-100 text-slate-500'}`}>{w.chartCount}</span>
              </button>
            );
          })}
          <button type="button" onClick={() => setAddingWashroom(true)} className="inline-flex items-center gap-1 rounded-full border border-dashed border-sky-300 px-4 py-2 text-sm font-semibold text-sky-700 transition hover:bg-sky-50">
            + Add washroom
          </button>
        </div>

        {washrooms.length === 0 ? (
          <p className="mt-4 rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-center text-sm text-slate-500">
            No washrooms yet. Use “+ Add washroom” to add the first one, then pick a month to generate its chart.
          </p>
        ) : (
          <div className="mt-5 flex flex-wrap items-end gap-3 border-t border-slate-100 pt-4">
            <div>
              <label htmlFor="cleaning-month" className="block text-xs font-semibold uppercase tracking-wide text-slate-500">Year &amp; month</label>
              <div className="mt-1.5 flex items-center gap-1.5">
                <button type="button" onClick={() => goToMonth(year, month - 1)} aria-label="Previous month" className="h-10 w-10 rounded-lg border border-slate-200 text-lg text-slate-600 hover:bg-slate-100">‹</button>
                <DatePicker
                  id="cleaning-month"
                  selected={new Date(year, month - 1, 1)}
                  onChange={(d) => { if (d) goToMonth(d.getFullYear(), d.getMonth() + 1); }}
                  showMonthYearPicker
                  dateFormat="MMMM yyyy"
                  minDate={new Date(2000, 0, 1)}
                  maxDate={new Date(2100, 11, 31)}
                  onChangeRaw={(e) => e?.preventDefault?.()}
                  wrapperClassName="w-44"
                  className="h-10 w-full cursor-pointer rounded-lg border border-slate-300 bg-white px-3 text-center text-sm font-semibold text-slate-900 outline-none focus:border-sky-600 focus:ring-2 focus:ring-sky-100"
                  popperPlacement="bottom-start"
                />
                <button type="button" onClick={() => goToMonth(year, month + 1)} aria-label="Next month" className="h-10 w-10 rounded-lg border border-slate-200 text-lg text-slate-600 hover:bg-slate-100">›</button>
              </div>
            </div>
            <button type="button" onClick={() => goToMonth(now.getFullYear(), now.getMonth() + 1)} className={`${btnNeutral} h-10`}>This month</button>
          </div>
        )}
      </section>

      {/* 2 · Year overview (the checklist) */}
      {washroom ? (
        <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-5 py-3">
            <div>
              <h3 className="text-base font-semibold text-slate-900">{washroom.name} · {year}</h3>
              <p className="text-xs text-slate-500">Pick a month to open it. ✓ means the signed scan is uploaded.</p>
            </div>
            <div className="flex items-center gap-1.5">
              <button type="button" onClick={() => setYear((y) => y - 1)} aria-label="Previous year" className="h-8 w-8 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-100">‹</button>
              <span className="w-12 text-center text-sm font-semibold text-slate-800">{year}</span>
              <button type="button" onClick={() => setYear((y) => y + 1)} aria-label="Next year" className="h-8 w-8 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-100">›</button>
            </div>
          </div>
          <ul className="grid grid-cols-2 gap-2 p-4 sm:grid-cols-3 lg:grid-cols-6">
            {MONTHS.map((name, i) => {
              const m = i + 1;
              const s = monthReady ? summaryByMonth.get(m) : undefined;
              const selected = m === month;
              const percent = s && s.totalDays ? Math.round((s.doneDays / s.totalDays) * 100) : 0;
              const pending = s && s.signedCount === 0 && monthIsOver(year, m);
              return (
                <li key={name}>
                  <button
                    type="button"
                    onClick={() => setMonth(m)}
                    aria-pressed={selected}
                    className={`flex w-full flex-col rounded-xl border p-3 text-left transition ${selected ? 'border-sky-500 bg-sky-50 ring-1 ring-sky-500' : 'border-slate-200 hover:border-sky-300 hover:bg-slate-50'}`}
                  >
                    <span className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-slate-800">{name.slice(0, 3)}</span>
                      {s && s.signedCount > 0 ? <span className="text-xs font-bold text-emerald-600" title="Signed copy uploaded">✓</span> : pending ? <span className="h-2 w-2 rounded-full bg-amber-400" title="Signed copy pending" /> : null}
                    </span>
                    {s ? (
                      <>
                        <span className="mt-1 text-[11px] text-slate-500">{s.doneDays}/{s.totalDays} days</span>
                        <span className="mt-1.5 block h-1.5 overflow-hidden rounded-full bg-slate-200">
                          <span className={`block h-full ${percent === 100 ? 'bg-emerald-500' : 'bg-sky-500'}`} style={{ width: `${percent}%` }} />
                        </span>
                      </>
                    ) : (
                      <span className="mt-1 text-[11px] text-slate-400">{monthReady ? 'Not generated' : '…'}</span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </section>
      ) : null}

      {/* 3 · The chart, or a prompt to generate it */}
      {washroom && monthReady && !current ? (
        <section className="rounded-2xl border border-dashed border-sky-300 bg-sky-50/50 px-6 py-10 text-center">
          <p className="text-base font-semibold text-slate-900">No chart for {washroom.name} in {MONTHS[month - 1]} {year}</p>
          <p className="mx-auto mt-1 max-w-md text-sm text-slate-500">Generate it to get a sheet with a row for every day of the month. You can fill it in at any time.</p>
          <button type="button" onClick={generate} disabled={generating} className="mt-5 rounded-lg bg-sky-600 px-6 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-sky-700 disabled:bg-sky-400">
            {generating ? 'Generating…' : `Generate ${MONTHS[month - 1]} ${year} chart`}
          </button>
        </section>
      ) : null}

      {washroom && current && !shownChart ? (
        <div className="h-48 animate-pulse rounded-2xl border border-slate-200 bg-white" />
      ) : null}

      {shownChart ? (
        <>
          <ChartSheet chart={shownChart} cleaners={cleaners} staff={staff} signedCount={signedCount} onPatchDay={patchDay} onFill={fillPeople} onFillAfter={fillAfter} onDelete={deleteChart} onCleanerAdded={(c) => setCleaners((prev) => [...prev, c].sort((a, b) => Number(b.isActive) - Number(a.isActive) || a.name.localeCompare(b.name)))} />
          <TvecDocuments
            key={shownChart.section}
            section={shownChart.section}
            sectionTitle={`${shownChart.washroomName} - Cleaning chart ${MONTHS[shownChart.month - 1]} ${shownChart.year}`}
            heading="Signed copy (scan)"
            intro="After the month ends, upload the scan of the chart with the actual signatures. Re-uploading keeps the earlier scans in the history."
            disabledReason={monthIsOver(shownChart.year, shownChart.month) ? undefined : `The signed scan can be uploaded after the month ends (${MONTHS[shownChart.month - 1]} ${shownChart.year}).`}
            onCountChange={(count) => setSummaries((prev) => prev.map((s) => (s.id === shownChart.id ? { ...s, signedCount: count } : s)))}
          />
        </>
      ) : null}

      <CleanerManager cleaners={cleaners} setCleaners={setCleaners} />

      {addingWashroom ? (
        <AddWashroomModal
          onClose={() => setAddingWashroom(false)}
          onAdded={(added, existed) => {
            setWashrooms((prev) => (prev.some((w) => w.id === added.id) ? prev : [...prev, added].sort((a, b) => a.name.localeCompare(b.name))));
            chooseWashroom(added.id);
            setAddingWashroom(false);
            toast(existed ? 'info' : 'success', existed ? 'That washroom already exists — selected' : 'Washroom added');
          }}
        />
      ) : null}
    </div>
  );
}
